// OTC 外部連携 URL の許可判定。最終認可は BE を正とし、FE では一次防御だけ行う。

const PRD_ALLOWED_HOSTS = ["satudora.co.jp"] as const;
const NON_PRD_ALLOWED_HOSTS = [...PRD_ALLOWED_HOSTS, "stg.satudora.co.jp", "dev.satudora.co.jp"] as const;
const OTC_ENTRY_PREFIXES = ["/r/pharmacy-otc-interview-requested/", "/r/pharmacy-otc-consent-requested/"] as const;
const OTC_INTERNAL_PREFIXES = ["/otc-interviews", "/otc-screenings", "/otc-guidance"] as const;
const OTC_INTERVIEW_PATH_PREFIX = "/otc-interviews/";

const getAllowedHosts = (): readonly string[] =>
  process.env.BUILD_ENV === "prd" ? PRD_ALLOWED_HOSTS : NON_PRD_ALLOWED_HOSTS;

export const isAllowedOtcRedirectUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;

  const allowed = getAllowedHosts();
  return allowed.includes(parsed.hostname);
};

const hasUnsafePathPattern = (value: string): boolean => {
  const [pathname] = value.startsWith("/") ? value.split(/[?#]/) : [new URL(value).pathname];
  const lower = pathname.toLowerCase();
  return (
    pathname.startsWith("//") ||
    pathname.includes("\\") ||
    lower.startsWith("/%5c") ||
    lower.includes("%5c") ||
    lower.includes("%2f%2f")
  );
};

const getSameOriginPath = (value: string): string | undefined => {
  if (!value) return undefined;

  try {
    if (hasUnsafePathPattern(value)) return undefined;
  } catch {
    return undefined;
  }

  if (value.startsWith("/")) return value;

  try {
    const parsed = new URL(value);
    const yqbUrl = process.env.MGDX_YQB_URL ? new URL(process.env.MGDX_YQB_URL) : undefined;

    if (!yqbUrl || parsed.protocol !== yqbUrl.protocol || parsed.host !== yqbUrl.host) {
      return undefined;
    }

    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return undefined;
  }
};

const decodeAsciiPercentEncoding = (value: string): string =>
  value.replace(/%([0-9a-fA-F]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));

const getComparablePathname = (path: string): string => {
  const [pathname] = path.split(/[?#]/);

  try {
    return decodeURIComponent(pathname);
  } catch {
    return decodeAsciiPercentEncoding(pathname);
  }
};

export const parseAllowedOtcEntryRedirectUrl = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;

  const path = getSameOriginPath(value);
  if (!path) return undefined;

  return OTC_ENTRY_PREFIXES.some((prefix) => path.startsWith(prefix)) ? path : undefined;
};

export const parseAllowedOtcEntryNavigateUrl = (value: string | null | undefined): string | undefined => {
  const path = parseAllowedOtcEntryRedirectUrl(value);
  if (!path) return undefined;

  return path.replace(/^\/r\//, "/j/");
};

export const isOtcEntryRedirectUrl = (value: string | null | undefined): boolean => {
  if (!value) return false;

  const path = getSameOriginPath(value);
  if (!path) return false;

  return getComparablePathname(path).toLowerCase().includes("pharmacy-otc-");
};

export const isOtcInternalRedirectUrl = (value: string | null | undefined): boolean => {
  if (!value) return false;

  const path = getSameOriginPath(value);
  if (!path) return false;

  const pathname = getComparablePathname(path);
  return OTC_INTERNAL_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

// サツドラ EC(オンラインストア)経由の OTC 導線かどうかの判定。入口ページと OTC 内部画面の両方を対象にする。
export const isOtcOnlineStoreRedirectUrl = (value: string | null | undefined): boolean =>
  isOtcEntryRedirectUrl(value) || isOtcInternalRedirectUrl(value);

/**
 * 要指導の会員情報登録の後に戻る問診画面の URL を検証する。
 * 自オリジン内かつ /otc-interviews/ 配下だけを許可し、それ以外は undefined にする
 */
export const parseAllowedOtcInterviewRedirectPath = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;

  const path = getSameOriginPath(value);
  if (!path) return undefined;

  // ドットセグメント (..) などで解決後のパスが変わる値は、問診画面の外を指しうるため拒否する
  const comparablePathname = getComparablePathname(path);
  try {
    if (new URL(comparablePathname, "https://placeholder.invalid").pathname !== comparablePathname) return undefined;
  } catch {
    return undefined;
  }

  return comparablePathname.startsWith(OTC_INTERVIEW_PATH_PREFIX) ? path : undefined;
};
